import { DownloadIcon, UploadIcon, XIcon } from "@primer/octicons-react";
import { Box, FormControl } from "@primer/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import ProgressBar from "devextreme-react/progress-bar";
import { mucPhaiThuApi } from "../../../api/mucPhaiThuApi";
import Files from "../../../components/files/Files";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Modal from "../../../components/ui/modal";
import { PopUpForm, PopUpFormActions } from "../../../components/pop-up-form/PopUpForm";
import Steps, { IStepData } from "../../../components/steps/Steps";
import Button from "../../../components/ui/button";
import Heading from "../../../components/ui/heading";
import { PlaceHolder } from "../../../components/ui/place-holder";
import { showNotify } from "../../../helpers/toast";
import Upload from "../../../components/upload/Upload";

type HoanPhiImportModalProps = {
  onClose: () => void;
  onSuccess: () => void;
  nam_hoc: string;
  dm_coso_id: number;
  animationOf: string;
};

type StepId = 1 | 2 | 3;

interface IUploadedFile {
  url: string;
  name: string;
  file_name: string;
}

const _steps: IStepData[] = [
  { id: 1, name: "Upload file", is_active: true },
  { id: 2, name: "Validate", is_active: false },
  { id: 3, name: "Import", is_active: false },
];

export const HoanPhiImportModal = (props: HoanPhiImportModalProps) => {
  const [stepId, setStepId] = useState<StepId>(1);
  const [uploadedFile, setUploadedFile] = useState<IUploadedFile | undefined>(undefined);
  const [isDataValid, setIsDataValid] = useState<"" | "success" | "error">("");
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState(0);

  const onClose = () => {
    window.animationHidePopup(props.animationOf || "");
    setTimeout(() => props.onClose(), 500);
  };

  const handleImport = async () => {
    if (!uploadedFile) return;
    setIsSaving(true);
    setProgress(0);

    // Simulate progress since we don't have a progress endpoint for hoan phi
    setProgress(10);
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev < 90 ? prev + 10 : prev));
    }, 400);

    try {
      const res = await mucPhaiThuApi.import_hoanphi({
        file_name: uploadedFile.url,
        sheet_index: 0,
        nam_hoc: props.nam_hoc,
        dm_coso_id: props.dm_coso_id,
        root_file_name: uploadedFile.name,
      });
      clearInterval(progressInterval);
      if (res.is_success) {
        setProgress(100);
        showNotify({ message: "Import thành công", type: "success" });
        setStepId(3);
        props.onSuccess();
      } else {
        showNotify({ message: res.message || "Có lỗi khi import", type: "error" });
      }
    } catch (err) {
      clearInterval(progressInterval);
      showNotify({ message: "Lỗi hệ thống khi import", type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const width = useMemo(() => {
    if (stepId === 1) {
      return "xlarge";
    }
    return "80%";
  }, [stepId]);

  return (
    <Modal
      title={`Import hoàn phí - Năm học ${props.nam_hoc}`}
      onClose={props.onClose}
      isOpen={true}
      width={width}
      height={"auto"}
    >
      <PopUpForm onSubmit={() => { }}>
        <Box>
          <Steps
            steps={_steps.map(x => ({
              ...x,
              is_active: x.id === stepId
            }))}
            onChangedStep={(step) => {
              if (step <= stepId) {
                setStepId(step as StepId);
              }
            }}
          />
        </Box>
        <Box sx={{ mt: 3 }}>
          {stepId === 1 && (
            <Box>
              <Box sx={{ mt: 2, mb: 3, display: "flex", justifyContent: "center" }}>
                <Button
                  text='Tải file mẫu'
                  variant='invisible'
                  leadingVisual={DownloadIcon}
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/templates/template_import_hoanphi.xlsx";
                    link.target = "_blank";
                    link.download = "template_import_hoanphi.xlsx";
                    link.click();
                  }}
                />
              </Box>
              <Box>
                {uploadedFile && (
                  <Box sx={{ mb: 2 }}>
                    <Heading text='File đã tải lên' />
                    <Files files={[uploadedFile]}
                      isPreviewImg={false}
                      onFileRemove={() => setUploadedFile(undefined)}
                    />
                  </Box>
                )}
                <Upload onUploadSuccess={(data) => {
                  setUploadedFile({ url: data.url, name: data.name, file_name: data.file_name });
                  setStepId(2);
                }} />
              </Box>
            </Box>
          )}

          {stepId === 2 && uploadedFile && (
            <Box>
              <ValidateDataResult
                file={uploadedFile}
                nam_hoc={props.nam_hoc}
                dm_coso_id={props.dm_coso_id}
                onValidDone={(status) => setIsDataValid(status)}
              />
            </Box>
          )}
        </Box>

        {isSaving && (
          <Box sx={{ mt: 3, px: 3 }}>
            <ProgressBar
              min={0}
              max={100}
              value={progress}
              statusFormat={(v) => `Đang import: ${Math.round(v * 100)}%`}
              showStatus={true}
            />
          </Box>
        )}

        <PopUpFormActions>
          <Button variant="default" onClick={onClose} leadingVisual={XIcon}>Đóng</Button>
          {stepId === 2 && (
            <Button
              variant="primary"
              leadingVisual={UploadIcon}
              onClick={handleImport}
              isLoading={isSaving}
              disabled={isDataValid !== "success"}
            >
              Import
            </Button>
          )}
        </PopUpFormActions>
      </PopUpForm>
    </Modal>
  );
};

// ---- ValidateDataResult ----

interface IValidateDataResultProps {
  file: IUploadedFile;
  nam_hoc: string;
  dm_coso_id: number;
  onValidDone: (status: "" | "success" | "error") => void;
}

const ValidateDataResult = (props: IValidateDataResultProps) => {
  const { file, nam_hoc, dm_coso_id, onValidDone } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState<"all" | "valid" | "invalid">("all");

  const errData = useMemo(
    () => dataSource.filter((x) => x.ma_loi && x.ma_loi !== ""),
    [dataSource]
  );

  const validData = useMemo(
    () => dataSource.filter((x) => !x.ma_loi || x.ma_loi === ""),
    [dataSource]
  );

  useEffect(() => {
    if (dataSource.length > 0 && errData.length === 0) {
      onValidDone("success");
    } else if (dataSource.length > 0) {
      onValidDone("error");
    } else {
      onValidDone("");
    }
  }, [errData, dataSource, onValidDone]);

  const handleRead = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await mucPhaiThuApi.readFiles_hoanphi({
        file_name: file.url,
        sheet_index: 0,
        nam_hoc: nam_hoc,
        dm_coso_id: dm_coso_id,
        root_file_name: file.name,
      });
      if (res.is_success) {
        setDataSource(
          (res.data?.data_uploaded || []).map((x: any) => ({
            ...x,
            ma_loi: x.Status,
          }))
        );
      } else {
        showNotify({ message: res.message || "Có lỗi khi đọc file", type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  }, [file.url, file.name, nam_hoc, dm_coso_id]);

  useEffect(() => {
    handleRead();
  }, [handleRead]);

  const columns: IDataGridColumn[] = useMemo(() => {
    const objectData = dataSource[0];
    const excludedFields = ["id", "ID", "Status", "ma_loi", "err"];

    const cols: IDataGridColumn[] = [
      {
        caption: "Kết quả",
        id: "Status",
        dataField: "Status",
        minWidth: 200,
        cellRender: (data: any) => {
          return (
            <>
              {data.Status && (
                <FormControl.Validation variant="error">
                  <Box sx={{ whiteSpace: "break-spaces" }} className="limit2Line">
                    <Box
                      style={{ whiteSpace: "pre-line", textAlign: "left" }}
                      dangerouslySetInnerHTML={{ __html: data.Status }}
                    />
                  </Box>
                </FormControl.Validation>
              )}
              {!data.Status && (
                <FormControl.Validation variant="success" />
              )}
            </>
          );
        },
      },
    ];

    for (const key in objectData) {
      if (objectData.hasOwnProperty(key) && !excludedFields.includes(key)) {
        cols.push({ dataField: key, caption: key, minWidth: 150 });
      }
    }

    return cols;
  }, [dataSource]);

  const displayData = useMemo(() => {
    if (filterStatus === "valid") return validData;
    if (filterStatus === "invalid") return errData;
    return dataSource;
  }, [filterStatus, validData, errData, dataSource]);

  const handleExportData = useCallback(() => {
    const exportData = displayData;
    if (exportData.length === 0) {
      showNotify({ message: "Không có dữ liệu để tải", type: "warning" });
      return;
    }

    // Create Excel file
    const headers = Object.keys(exportData[0]).filter(k => k !== "ma_loi");
    const dataForExport = exportData.map((row: any) => {
      const newRow: any = {};
      headers.forEach(h => {
        newRow[h] = row[h];
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    const filterName = filterStatus === "all" ? "all" : filterStatus === "valid" ? "valid" : "invalid";
    XLSX.writeFile(workbook, `import_hoanphi_${filterName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [displayData, filterStatus]);

  return (
    <Box>
      {isLoading ? (
        <PlaceHolder line_number={8} />
      ) : (
        <>
          <DataGrid
            dataSource={displayData}
            isLoading={isLoading}
            subTitle={<Box sx={{ display: "flex", gap: 2 }}>
              <Box
                onClick={() => setFilterStatus("all")}
                sx={{
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  border: filterStatus === "all" ? "1px solid #1f883d" : "1px solid #d0d7de",
                  backgroundColor: filterStatus === "all" ? "#dafbe1" : "transparent",
                  color: filterStatus === "all" ? "#1a7f37" : "#57606a",
                  fontWeight: filterStatus === "all" ? 600 : 400,
                  fontSize: "13px"
                }}
              >
                Tất cả ({dataSource.length})
              </Box>
              <Box
                onClick={() => setFilterStatus("valid")}
                sx={{
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  border: filterStatus === "valid" ? "1px solid #1a7f37" : "1px solid #d0d7de",
                  backgroundColor: filterStatus === "valid" ? "#dafbe1" : "transparent",
                  color: filterStatus === "valid" ? "#1a7f37" : "#57606a",
                  fontWeight: filterStatus === "valid" ? 600 : 400,
                  fontSize: "13px"
                }}
              >
                Hợp lệ ({validData.length})
              </Box>
              <Box
                onClick={() => setFilterStatus("invalid")}
                sx={{
                  cursor: "pointer",
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  border: filterStatus === "invalid" ? "1px solid #cf222e" : "1px solid #d0d7de",
                  backgroundColor: filterStatus === "invalid" ? "#ffebe9" : "transparent",
                  color: filterStatus === "invalid" ? "#cf222e" : "#57606a",
                  fontWeight: filterStatus === "invalid" ? 600 : 400,
                  fontSize: "13px"
                }}
              >
                Không hợp lệ ({errData.length})
              </Box>
            </Box>}
            actionComponent={
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box
                  onClick={handleExportData}
                  sx={{
                    cursor: "pointer",
                    px: 2,
                    py: 1,
                    borderRadius: "6px",
                    border: "1px solid #d0d7de",
                    backgroundColor: "#f6f8fa",
                    color: "#24292f",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <DownloadIcon /> Export
                </Box>
              </Box>
            }
            columns={columns}
            height={window.innerHeight - 300}
            filterRow={{ enable: true }}
            searchEnable={true}
            paging={{ enable: true, pageSize: 50, pageSizeItems: [50, 200, 500, 1000] }}
          />
        </>
      )}
    </Box>
  );
};
